import classNames from 'classnames'

type Gap = 0 | 1 | 2 | 3 | 4 | 5 | 6

export type SpaceProps = {
  /**
   * Default: 'horizontal'
   */
  direction?:
    | 'horizontal'
    | 'vertical'
    | 'horizontal-reverse'
    | 'vertical-reverse'
  /**
   * Default: 'center' for horizontal, 'start' for vertical
   */
  align?: 'start' | 'center' | 'end' | 'normal' | 'baseline'
  /**
   * Default: 'start'
   */
  justify?: 'start' | 'center' | 'end' | 'between' | 'around'
  /**
   * Gap sizes as defined by Blueprint. Default: 1. Can be a single value or an array of two values for horizontal and vertical gaps.
   */
  gap?: Gap | [Gap, Gap]
  /**
   * Default: false
   */
  wrap?: boolean
  /**
   * Default: false
   * If true, the component will be rendered as an inline-flex element.
   */
  inline?: boolean
  grow?: boolean

  children: React.ReactNode
} & React.HTMLAttributes<HTMLElement>

/**
 * A component to use when you want to space out elements in a row or column.
 * Example:
 * <Space>
 *      <Button>Button 1</Button>
 *      <Button>Button 2</Button>
 * </Space>
 */
export const Space = ({
  direction = 'horizontal',
  align: alignProp,
  gap = 1,
  wrap = false,
  inline = false,
  className,
  children,
  justify,
  grow,
  ...rest
}: SpaceProps) => {
  const defaultAlign = direction === 'horizontal' ? 'center' : 'normal'
  const align = alignProp ?? defaultAlign

  const gapX = Array.isArray(gap) ? gap[0] : (gap ?? 1)
  const gapY = Array.isArray(gap) ? gap[1] : (gap ?? 1)

  const gapClass = `gap-x-${gapX} gap-y-${gapY}`

  return (
    <div
      className={classNames(
        'd-flex',
        gapClass,
        {
          'd-inline-flex': !!inline,
          'flex-wrap': wrap,
          'flex-column': direction === 'vertical',
          'flex-column-reverse': direction === 'vertical-reverse',
          'flex-row-reverse': direction === 'horizontal-reverse',
          'align-items-center': align === 'center',
          'align-items-start': align === 'start',
          'align-items-end': align === 'end',
          'align-items-normal': align === 'normal',
          'align-items-baseline': align === 'baseline',
          'justify-content-between': justify === 'between',
          'justify-content-end': justify === 'end',
          'justify-content-center': justify === 'center',
          'justify-content-around': justify === 'around',
          'flex-grow-1': grow,
          'flex-grow-0': grow === false,
        },
        className
      )}
      {...rest}
    >
      {children}
    </div>
  )
}
